// ⚠️ This list MUST stay byte-identical to the backend twin at
//   lixani-dynamodb-api/3-API/src/server/utils/permission/accessPermissions.ts
// Any added entry, renamed feature, or changed accessKey/permPrefix must be
// applied to BOTH files in the same commit/PR. Divergence will cause the UI
// and the API to enforce different rules and produce confusing 403s.

// Source of truth for "main / gatekeeper" access permission per feature.
//
// Used by:
//  1. EditUserPermissionsDialog → PermissionCard, to render the master toggle
//     on each feature card (`returnAccessPermissionByFeature`).
//  2. The permission selectors in personalPermissionsSelectors.js, to gate
//     every sub-permission behind its feature's gatekeeper
//     (`isGatekeeperClosed`). If the gatekeeper is off, every sub-perm under
//     that feature evaluates to false regardless of stored value.
//
// `feature` matches the canonical feature keys used by the dialog's iteration
// (returnTogglableProjectPermissionsByFeature / returnTogglableEmployerPermissionsByFeature).
// For features whose perm names don't simply start with `${feature}_`, an
// explicit `permPrefix` overrides the default.

const ACCESS_PERMISSIONS = [
  // Project-level features with explicit `_access` perms
  { feature: 'forms', accessKey: 'forms_access' },
  { feature: 'gallery', accessKey: 'gallery_access' },
  { feature: 'bulletin', accessKey: 'bulletin_board_access' },
  { feature: 'project_bank', accessKey: 'project_bank_access' },
  {
    feature: 'project_bank_private_folder',
    accessKey: 'project_bank_private_folder_access',
  },
  { feature: 'project_map', accessKey: 'project_map_access' },
  { feature: 'expense_tracking', accessKey: 'expense_tracking_access' },
  { feature: 'defects', accessKey: 'defects_defect_lists_access' },
  {
    feature: 'firestop_documentation',
    accessKey: 'firestop_documentation_access',
  },
  { feature: 'work_calendar', accessKey: 'work_calendar_access' },
  { feature: 'customer_registry', accessKey: 'customer_registry_access' },
  { feature: 'project_chat', accessKey: 'project_chat_access' },
  { feature: 'renolog', accessKey: 'renolog_access' },
  { feature: 'nerobotti', accessKey: 'nerobotti_access' },
  { feature: 'employee_features', accessKey: 'employee_features_access' },

  // Features whose entry-point perm isn't named `_access`.
  // Note: there is intentionally no entry for the `project` feature group.
  // The perms grouped under it in the dialog (project_create, project_edit,
  // project_status_edit, project_approvals_manage, project_map_access,
  // view_subprojects) are unrelated functionally — they share the group only
  // for admin readability. For example project_approvals_manage controls
  // approvals inside the work_calendar feature, not anything within `project`.
  {
    feature: 'timelines',
    permPrefix: 'project_timelines_',
    accessKey: 'project_timelines_view',
  },
  { feature: 'areaplan', accessKey: 'areaplan_view' },
  { feature: 'organization_chart', accessKey: 'organization_chart_view' },
  { feature: 'project_email', accessKey: 'project_email_read' },

  // Per-form-type access perms (10 form types)
  {
    feature: 'forms_worksite_status_declarations',
    accessKey: 'forms_worksite_status_declarations_access',
  },
  {
    feature: 'forms_non_conformance_reports',
    accessKey: 'forms_non_conformance_reports_access',
  },
  { feature: 'forms_tr_meters', accessKey: 'forms_tr_meters_access' },
  {
    feature: 'forms_self_supervision_memos',
    accessKey: 'forms_self_supervision_memos_access',
  },
  {
    feature: 'forms_self_handover_forms',
    accessKey: 'forms_self_handover_forms_access',
  },
  {
    feature: 'forms_worksite_journals',
    accessKey: 'forms_worksite_journals_access',
  },
  {
    feature: 'forms_hot_work_permit_forms',
    accessKey: 'forms_hot_work_permit_forms_access',
  },
  {
    feature: 'forms_weekly_review_forms',
    accessKey: 'forms_weekly_review_forms_access',
  },
  {
    feature: 'forms_personnel_lift_usage_permits',
    accessKey: 'forms_personnel_lift_usage_permits_access',
  },
  {
    feature: 'forms_substation_commissioning_inspection_reports',
    accessKey: 'forms_substation_commissioning_inspection_reports_access',
  },
  {
    feature: 'forms_template_forms',
    accessKey: 'forms_template_forms_access',
  },
  {
    feature: 'forms_orientation_forms',
    accessKey: 'forms_orientation_forms_access',
  },
];

const getPermPrefix = (entry) => entry.permPrefix || `${entry.feature}_`;

// Sorted longest-prefix-first so e.g. `forms_hot_work_permit_forms_` beats
// the more generic `forms_` when matching `forms_hot_work_permit_forms_edit_all`.
const gatekeeperByPrefix = ACCESS_PERMISSIONS.map((entry) => ({
  prefix: getPermPrefix(entry),
  accessKey: entry.accessKey,
})).sort((a, b) => b.prefix.length - a.prefix.length);

export const returnAccessPermissionByFeature = (feature) => {
  const entry = ACCESS_PERMISSIONS.find((e) => e.feature === feature);
  return entry ? entry.accessKey : null;
};

export const findGatekeeperFor = (permission) => {
  if (typeof permission !== 'string' || !permission) return null;
  for (const { prefix, accessKey } of gatekeeperByPrefix) {
    if (permission === accessKey) {
      // Per-form-type access perms (`forms_<type>_access`) chain up to
      // `forms_access` so that toggling top-level Lomakkeet access off
      // cascades to every form sub-feature. Other top-level access perms
      // (gallery_access, project_bank_access, …) remain self-ungating.
      if (accessKey.startsWith('forms_') && accessKey !== 'forms_access') {
        return 'forms_access';
      }
      return null;
    }
    if (permission.startsWith(prefix)) return accessKey;
  }
  return null;
};

export const isGatekeeperClosed = (permission, permissions) => {
  // Walk the gatekeeper chain — a sub-perm is closed if ANY gatekeeper
  // above it (direct or transitive) is not granted. Required because
  // per-form-type access perms chain through `forms_access`; if a sub-perm's
  // direct gatekeeper is on but `forms_access` above it is off, the sub-perm
  // must still be closed.
  let current = permission;
  let gatekeeper = findGatekeeperFor(current);
  while (gatekeeper) {
    if (permissions?.[gatekeeper] !== true) return true;
    current = gatekeeper;
    gatekeeper = findGatekeeperFor(current);
  }
  return false;
};

export { ACCESS_PERMISSIONS };
